import { debounce } from '@src/utils/functions/utils'

import {
  DEFAULT_DEBOUNCE_PERIOD,
  DEFAULT_EVENT_LIMIT,
  EVENT_PUBLISHER_DEBOUNCE_PERIOD,
} from './batched-analytics.constants'
import { BatchedAnalyticsStream } from './batched-analytics.types'

export class BatchedAnalytics {
  private readonly batchedAnalyticsStream: BatchedAnalyticsStream

  constructor({ batchedAnalyticsStream }: { batchedAnalyticsStream: BatchedAnalyticsStream }) {
    this.batchedAnalyticsStream = batchedAnalyticsStream
  }

  initializeBatchedAnalyticsEvent({
    tracker,
    debouncePeriod,
    eventLimit,
    eventList,
  }: {
    tracker: string
    debouncePeriod?: number
    eventLimit?: number
    eventList?: any
  }) {
    if (tracker && !this.batchedAnalyticsStream[tracker]) {
      this.batchedAnalyticsStream[tracker] = {
        tracker,
        debouncePeriod: debouncePeriod ?? DEFAULT_DEBOUNCE_PERIOD,
        eventLimit: eventLimit ?? DEFAULT_EVENT_LIMIT,
        eventList: eventList || [],
        subscribers: {},
        debouncedPublisher: debounce(args => {
          this.publishEventsToAnalytics(args)
        }, EVENT_PUBLISHER_DEBOUNCE_PERIOD),
        isPublishing: false,
      }
    }
  }

  pushEventToBatchedAnalyticsStream({
    tracker,
    event,
    retryCount = 0,
  }: {
    tracker: string
    event: any
    retryCount?: number
  }) {
    if (tracker) {
      if (this.batchedAnalyticsStream[tracker]) {
        const trackerForPublish = this.batchedAnalyticsStream[tracker]
        trackerForPublish.eventList.push(event)
        clearTimeout(trackerForPublish.timeoutId)
        trackerForPublish.timeoutId = undefined

        // Publishing with length check
        const currentEventListLength = trackerForPublish.eventList.length
        if (
          trackerForPublish.eventLimit &&
          currentEventListLength == trackerForPublish.eventLimit &&
          !trackerForPublish.isPublishing
        ) {
          trackerForPublish.isPublishing = true
          trackerForPublish.debouncedPublisher({
            tracker,
            batchedAnalyticsStream: this.batchedAnalyticsStream,
          })
        } else if (trackerForPublish.debouncePeriod && !trackerForPublish.isPublishing) {
          // Publishing with debouncing
          // trackerForPublish.isPublishing
          trackerForPublish.timeoutId = setTimeout(() => {
            trackerForPublish.debouncedPublisher({
              tracker,
              batchedAnalyticsStream: this.batchedAnalyticsStream,
            })
          }, trackerForPublish.debouncePeriod)
        }
      } else {
        if (retryCount) {
          this.initializeBatchedAnalyticsEvent({ tracker })
          this.pushEventToBatchedAnalyticsStream({ tracker, event, retryCount: retryCount - 1 })
        } else {
          throw new Error(`Tracker must be initialized before event is added : ${tracker}`)
        }
      }
    }
  }

  publishEventsToAnalytics({
    tracker,
    batchedAnalyticsStream,
  }: {
    tracker: string
    batchedAnalyticsStream: BatchedAnalyticsStream
  }) {
    if (tracker) {
      const trackerForPublish = batchedAnalyticsStream[tracker]
      if (trackerForPublish) {
        const currentEvents = [...trackerForPublish.eventList]
        if (batchedAnalyticsStream[tracker]) batchedAnalyticsStream[tracker].eventList = []
        Object.keys(trackerForPublish.subscribers).forEach(subscriberId => {
          trackerForPublish.subscribers[subscriberId]?.callback({
            tracker,
            events: currentEvents,
          })
        })
        trackerForPublish.isPublishing = false
      } else {
        throw new Error(`Tracker must be initialized before events are published : ${tracker}`)
      }
    }
  }

  trackBatchedAnalytics({
    id,
    tracker,
    callback,
  }: {
    id: string
    tracker: string
    callback: ({ tracker, events }: { tracker: string; events: any[] }) => void
  }) {
    if (this.batchedAnalyticsStream[tracker]) {
      const currentSubscribers = this.batchedAnalyticsStream[tracker].subscribers
      if (!currentSubscribers[id]) {
        this.batchedAnalyticsStream[tracker].subscribers[id] = { id, callback }
      }
    } else {
      throw new Error(`Cannot add subscriber for id ${id}. Tracker is not initialised yet for : ${tracker}`)
    }
  }
}

const batchedAnalytics = new BatchedAnalytics({ batchedAnalyticsStream: {} })

export default batchedAnalytics
